/**
 * CardGrid Component
 *
 * A responsive grid/list layout container for displaying item cards.
 * Supports both grid and list view modes with responsive columns.
 */

import { memo } from 'react';
import { cn } from '@/utils/cn';
import { ItemCard } from '@/components/ItemCard';
import { EmptyState } from '@/components/EmptyState';
import type { Item, ViewMode, Category, TaskItem } from '@/types';

// ============================================
// TYPES
// ============================================

export interface CardGridProps {
  /** Array of items to display */
  items: Item[];
  /** Current view mode (grid or list) */
  view: ViewMode;
  /** Handler when an item is clicked to view */
  onItemClick: (item: Item) => void;
  /** Handler when edit is requested */
  onEditItem: (item: Item) => void;
  /** Handler when delete is requested */
  onDeleteItem: (item: Item) => void;
  /** Handler when favorite is toggled */
  onToggleFavorite: (item: Item) => void;
  /** Handler when a tag is clicked */
  onTagClick?: ((tag: string) => void) | undefined;
  /** Handler when task completion is toggled */
  onToggleComplete?: ((item: TaskItem, completed: boolean) => void) | undefined;
  /** Current category (for empty state message) */
  category?: Category | undefined;
  /** Current search query (for empty state message) */
  searchQuery?: string | undefined;
  /** Handler for add button in empty state */
  onAddClick?: (() => void) | undefined;
  /** Additional class names */
  className?: string | undefined;
  /** Whether data is loading */
  isLoading?: boolean | undefined;
}

// ============================================
// LOADING SKELETON
// ============================================

function GridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-lg border border-gray-200 bg-white p-4">
          <div className="h-32 rounded-md bg-gray-200 mb-4" />
          <div className="h-4 rounded bg-gray-200 w-3/4 mb-2" />
          <div className="h-3 rounded bg-gray-200 w-1/2 mb-4" />
          <div className="flex gap-2">
            <div className="h-5 w-12 rounded-full bg-gray-200" />
            <div className="h-5 w-16 rounded-full bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ListSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-3"
        >
          <div className="h-8 w-8 rounded-md bg-gray-200" />
          <div className="flex-1">
            <div className="h-4 rounded bg-gray-200 w-1/3 mb-2" />
            <div className="flex gap-2">
              <div className="h-4 w-12 rounded-full bg-gray-200" />
              <div className="h-4 w-16 rounded-full bg-gray-200" />
            </div>
          </div>
          <div className="h-4 w-20 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

function CardGridComponent({
  items,
  view,
  onItemClick,
  onEditItem,
  onDeleteItem,
  onToggleFavorite,
  onTagClick,
  onToggleComplete,
  category,
  searchQuery,
  onAddClick,
  className,
  isLoading,
}: CardGridProps) {
  // Loading state
  if (isLoading) {
    return view === 'grid' ? <GridSkeleton /> : <ListSkeleton />;
  }

  // Empty state
  if (items.length === 0) {
    return <EmptyState category={category} searchQuery={searchQuery} onAddClick={onAddClick} />;
  }

  // Grid view
  if (view === 'grid') {
    return (
      <div
        className={cn(
          'grid grid-cols-1 gap-4',
          'sm:grid-cols-2',
          'lg:grid-cols-3',
          'xl:grid-cols-4',
          className
        )}
        role="list"
        aria-label="Items grid"
      >
        {items.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            view="grid"
            onView={onItemClick}
            onEdit={onEditItem}
            onDelete={onDeleteItem}
            onToggleFavorite={onToggleFavorite}
            onTagClick={onTagClick}
            onToggleComplete={onToggleComplete}
          />
        ))}
      </div>
    );
  }

  // List view
  return (
    <div className={cn('flex flex-col gap-2', className)} role="list" aria-label="Items list">
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          view="list"
          onView={onItemClick}
          onEdit={onEditItem}
          onDelete={onDeleteItem}
          onToggleFavorite={onToggleFavorite}
          onTagClick={onTagClick}
          onToggleComplete={onToggleComplete}
        />
      ))}
    </div>
  );
}

// Memoize for performance
export const CardGrid = memo(CardGridComponent);
